import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RotateCcw, Save, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  ErrorState,
  FormActions,
  FormField,
  FormGrid,
  Loading,
  PageHeader,
  Panel,
  RadioGroup,
  StatusBadge,
  TextField,
  useConfirm,
  useToast,
} from '@/components';
import { ORGANIZATION_TYPES } from '@/auth/types';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect } from '@/features/masters/components/BusinessOperatorSelect';
import { errorMessage } from '@/lib/api';
import { applyApiErrors } from '@/lib/form';
import { formatDateTime } from '@/lib/format';
import { useCreateUser, useDeleteUser, useResetUserMfa, useUpdateUser, useUser, type UserDto } from './api';
import { createUserSchema, editUserSchema, emptyUserForm, type UserFormValues } from './schema';
import './admin-users.css';

/** /admin/users/new と /admin/users/:id を兼ねる登録・編集画面 */
export function UserFormPage() {
  const { id } = useParams();
  const userId = id ? Number(id) : null;
  const { data, isPending, error, refetch } = useUser(userId);

  if (userId != null) {
    if (isPending) return <Loading block />;
    if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  }
  return <UserForm key={userId ?? 'new'} user={userId != null ? data : undefined} />;
}

function toForm(u: UserDto): UserFormValues {
  return {
    ...emptyUserForm,
    loginId: u.loginId,
    name: u.name,
    organizationType: u.organizationType,
    businessOperatorId: u.businessOperatorId,
    isActive: u.isActive,
    mfaRequired: u.mfaRequired,
  };
}

function UserForm({ user }: { user?: UserDto }) {
  const isNew = !user;
  const me = useCurrentUser();
  const navigate = useNavigate();
  const { year, yearPath } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser(user?.id ?? 0);
  const deleteUser = useDeleteUser();
  const resetMfa = useResetUserMfa(user?.id ?? 0);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UserFormValues>({
    resolver: zodResolver(isNew ? createUserSchema : editUserSchema),
    defaultValues: user ? toForm(user) : emptyUserForm,
  });

  const orgType = watch('organizationType');
  useEffect(() => {
    if (orgType !== '漁協') setValue('businessOperatorId', null);
  }, [orgType, setValue]);

  const listPath = yearPath('admin/users');
  const isSelf = user?.id === me.id;

  const onSubmit = handleSubmit(async ({ passwordConfirm: _c, password, ...rest }) => {
    try {
      if (isNew) {
        await createUser.mutateAsync({ ...rest, password });
        toast.success('ユーザーを登録しました', `${rest.name}（${rest.loginId}）`);
      } else {
        await updateUser.mutateAsync({ ...rest, ...(password ? { password } : {}) });
        toast.success('ユーザー情報を更新しました');
      }
      navigate(listPath);
    } catch (e) {
      if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
    }
  });

  const onDelete = async () => {
    if (!user) return;
    const ok = await confirm({
      title: 'ユーザーを削除しますか？',
      message: `${user.name}（${user.loginId}）を削除します。\nこの操作は取り消せません。利用を止めるだけなら「有効」のチェックを外してください。`,
      tone: 'danger',
      confirmLabel: '削除する',
    });
    if (!ok) return;
    try {
      await deleteUser.mutateAsync(user.id);
      toast.success('ユーザーを削除しました');
      navigate(listPath);
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const onResetMfa = async () => {
    if (!user) return;
    const ok = await confirm({
      title: '2段階認証をリセットしますか？',
      message: `${user.name}（${user.loginId}）の認証アプリの登録とリカバリーコードを削除し、ログイン中のセッションを終了させます。\nスマートフォンの紛失・機種変更などで本人が確認コードを入力できない場合に行ってください（本人確認を行ってから実施してください）。\n必須のユーザーは、次回ログイン時に認証アプリの登録をやり直します。`,
      tone: 'danger',
      confirmLabel: 'リセットする',
    });
    if (!ok) return;
    try {
      await resetMfa.mutateAsync();
      toast.success('2段階認証をリセットしました', user.mfaRequired ? '次回ログイン時に認証アプリの登録が必要です。' : undefined);
    } catch (e) {
      toast.error('リセットできませんでした', errorMessage(e));
    }
  };

  const onCancel = async () => {
    if (isDirty && !(await confirm({ title: '入力内容を破棄しますか？', message: '保存していない変更は失われます。', confirmLabel: '破棄して戻る', tone: 'danger' }))) return;
    navigate(listPath);
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        eyebrow="システム管理 ／ ユーザー管理"
        title={isNew ? 'ユーザー新規登録' : 'ユーザー編集'}
        meta={user && (user.isActive ? <StatusBadge tone="success" dot>有効</StatusBadge> : <StatusBadge dot>無効</StatusBadge>)}
        description={user ? `最終ログイン：${formatDateTime(user.lastLoginAt) || 'なし'}　／　更新日時：${formatDateTime(user.updatedAt)}` : undefined}
      />

      <Panel title="基本情報">
        <FormGrid columns={2}>
          <FormField label="ログインID" required error={errors.loginId?.message} hint="半角英数字と . _ @ - （3〜64文字）">
            <TextField autoComplete="off" width="md" {...register('loginId')} />
          </FormField>
          <FormField label="氏名" required error={errors.name?.message}>
            <TextField width="md" {...register('name')} />
          </FormField>
          <FormField label="組織区分" required error={errors.organizationType?.message} htmlFor="org-type">
            <Controller
              control={control}
              name="organizationType"
              render={({ field }) => (
                <RadioGroup
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isSelf}
                  options={ORGANIZATION_TYPES.map((t) => ({ value: t, label: t }))}
                />
              )}
            />
          </FormField>
          <FormField
            label="事業実施者"
            required={orgType === '漁協'}
            error={errors.businessOperatorId?.message}
            hint={orgType === '漁協' ? '所属する漁協（事業実施者）を選択' : '漁協ユーザーのみ指定します'}
          >
            <Controller
              control={control}
              name="businessOperatorId"
              render={({ field }) => (
                <BusinessOperatorSelect
                  fiscalYear={year}
                  includeInactive
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="選択してください"
                  disabled={orgType !== '漁協'}
                  invalid={!!errors.businessOperatorId}
                />
              )}
            />
          </FormField>
          <FormField label="利用状態" hint={isSelf ? '自分自身は無効にできません' : '無効にするとログインできなくなります'}>
            <Checkbox label="有効" disabled={isSelf} {...register('isActive')} />
          </FormField>
        </FormGrid>
      </Panel>

      <Panel
        title="2段階認証"
        description="必須にしたユーザーは、ログイン時にパスワードに加えて認証アプリの確認コードが必要になります（未登録の場合は次回ログイン時に登録を求めます）。任意のユーザーも本人が有効にできます。"
      >
        <FormGrid columns={2}>
          <FormField label="2段階認証の設定" htmlFor="mfa-required">
            <Controller
              control={control}
              name="mfaRequired"
              render={({ field }) => (
                <RadioGroup
                  name={field.name}
                  value={field.value ? 'required' : 'optional'}
                  onChange={(v) => field.onChange(v === 'required')}
                  options={[
                    { value: 'optional', label: '任意' },
                    { value: 'required', label: '必須' },
                  ]}
                />
              )}
            />
          </FormField>
          {user && (
            <FormField label="登録状態">
              <span className="bs-user-mfa">
                {user.mfaEnabled ? (
                  <>
                    <StatusBadge tone="success" dot>有効</StatusBadge>
                    <span className="bs-num">{formatDateTime(user.mfaEnabledAt)} 登録</span>
                  </>
                ) : (
                  <StatusBadge dot>未設定</StatusBadge>
                )}
                {user.mfaEnabled && (
                  <Button size="sm" variant="danger" icon={<RotateCcw size={14} />} onClick={() => void onResetMfa()} loading={resetMfa.isPending}>
                    2段階認証をリセット
                  </Button>
                )}
              </span>
            </FormField>
          )}
        </FormGrid>
      </Panel>

      <Panel title="パスワード" description={isNew ? '初期パスワードを設定し、利用者へ安全な方法で伝えてください。' : '変更する場合のみ入力してください。'}>
        <FormGrid columns={2}>
          <FormField label="パスワード" required={isNew} error={errors.password?.message} hint="8文字以上">
            <TextField type="password" autoComplete="new-password" width="md" {...register('password')} />
          </FormField>
          <FormField label="パスワード（確認）" required={isNew} error={errors.passwordConfirm?.message}>
            <TextField type="password" autoComplete="new-password" width="md" {...register('passwordConfirm')} />
          </FormField>
        </FormGrid>
      </Panel>

      <FormActions>
        {!isNew && !isSelf && (
          <Button variant="danger" icon={<Trash2 size={16} />} onClick={onDelete} loading={deleteUser.isPending} style={{ marginRight: 'auto' }}>
            削除
          </Button>
        )}
        <Button onClick={onCancel}>キャンセル</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={isSubmitting}>
          {isNew ? '登録する' : '保存する'}
        </Button>
      </FormActions>
    </form>
  );
}
